import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {map, timeout} from 'rxjs/operators';
import {ClientConfig} from '@app/client-config';
import {ConfigService} from '@services/infra/config.service';
import {ErrorService} from '@services/error.service';
import {ApplicationResource} from '@services/resource/application-resource';
import {ProductStatus} from '@services/model/product';
import {StatusResource} from '@services/resource/status-resource';
import {UUID} from '@util/types';
import {LoggingService} from '@services/logging.service';
import {Application} from '@services/model/application';
import {optional} from '@util/http';
import {Option} from 'fp-ts/lib/Option';

@Injectable({
  providedIn: 'root',
})
export class ApplicationService {
  constructor(
    private readonly http: HttpClient,
    private readonly conf: ConfigService,
    private readonly errors: ErrorService,
    private readonly logger: LoggingService,
  ) {}

  public readApplication(productId: string, cycleId: string): Observable<Option<Application>> {
    const url = `${this.conf.backendUrl}/v1/application/${productId}/${cycleId}`;
    return this.http.get<ApplicationResource>(url).pipe(
      timeout(ClientConfig.timeout),
      map((r) => Application.fromResource(r)),
      optional(),
      this.errors.withErrorHandler(),
    );
  }

  public approveProduct(productId: string, cycleId: string): Observable<void> {
    return this.updateStatus(productId, cycleId, {status: ProductStatus.approved});
  }

  public rejectProduct(productId: string, cycleId: string, comment?: string): Observable<void> {
    return this.updateStatus(productId, cycleId, {status: ProductStatus.denied, comment});
  }

  public resetProduct(productId: string, cycleId: string, comment?: string): Observable<void> {
    return this.updateStatus(productId, cycleId, {status: ProductStatus.created, comment});
  }

  public updateStatus(productId: UUID, cycleId: UUID, status: StatusResource): Observable<void> {
    return this.putStatus(productId, cycleId, status).pipe(timeout(ClientConfig.timeout), this.errors.withErrorHandler());
  }

  private putStatus(productId: string, cycleId: string, status: StatusResource): Observable<void> {
    const url = `${this.conf.backendUrl}/v1/application/${productId}/${cycleId}/status`;
    return this.http.put<void>(url, status).pipe(timeout(ClientConfig.timeout), this.errors.withErrorHandler());
  }

  public requestApproval(productId: string, cycleId: string): Observable<void> {
    this.logger.logInfo('requesting approval', {productId, cycleId});
    const url = `${this.conf.backendUrl}/v1/application/${productId}/publicationRequest/${cycleId}`;
    return this.http.put<void>(url, {}).pipe(timeout(ClientConfig.timeout), this.errors.withErrorHandler());
  }
}
